import { useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  VStack,
  Button,
  FormControl,
  Input,
  Alert,
  AlertTitle,
  FormErrorMessage,
  FormLabel,
  CloseButton,
  FormErrorIcon,
  AlertIcon
} from "@/storybook-components";
import type { SyntheticEvent, ChangeEvent } from "react";

import {
  setStatus,
  setError,
  setUserEmail
} from "@/redux/features/users/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import { submitEmail } from "@/redux/features/users/thunk";

const Form = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.users);
  const [isValid, setIsValid] = useState(true);
  const [isAlertClosed, setIsAlertClosed] = useState(false);
  const [email, setEmail] = useState("");

  const validate = string().email().required();

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    await dispatch(submitEmail());
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    dispatch(setError({ error: null }));
    const isValid = await validate.isValid(e.target.value);
    dispatch(setStatus({ status: "idle" }));
    setIsAlertClosed(false);
    setIsValid(isValid && e.target.value.length > 0);
    if (isValid) {
      dispatch(setUserEmail({ email: e.target.value }));
    }
  };

  return (
    <VStack my={6} spacing={6} as="form" noValidate onSubmit={handleSubmit}>
      <FormControl isInvalid={!isValid} isDisabled={status === "saving"}>
        <FormLabel>{translate("common.email")}</FormLabel>
        <Input
          type="email"
          data-testid="emailInput"
          value={email}
          onChange={handleChange}
        />
        <FormErrorMessage data-testid="emailInputError">
          <FormErrorIcon />
          {translate("users.invalidEmail")}
        </FormErrorMessage>
      </FormControl>
      {(status === "saved" || status === "reject") && !isAlertClosed && (
        <Alert status="success" alignItems="flex-start">
          <AlertIcon />
          <AlertTitle>{translate("users.resetPasswordInfo")}</AlertTitle>
          <CloseButton size="sm" onClick={() => setIsAlertClosed(true)} />
        </Alert>
      )}
      <Button
        variant="primary"
        data-testid="submitButton"
        type="submit"
        width="full"
        isLoading={status === "saving"}
        isDisabled={
          !isValid ||
          !email.length ||
          status === "saved" ||
          status === "reject" ||
          isAlertClosed
        }
      >
        {translate("common.submit")}
      </Button>
    </VStack>
  );
};

export default Form;
